import React, {
  useState,
  useEffect,
  useRef,
  ReactNode,
  useCallback,
} from "react";

import { IconChevronLeft, IconChevronRight } from "@at/design-system-icons";

import * as Styled from "./slider.styled";

export type onSliderUpdateHandler = (index: number) => void;

interface IGallerySliderProps {
  children: ReactNode[];
  initialSlide: number;
  onSliderUpdate?: onSliderUpdateHandler;
  showImageCount?: boolean;
}

const BATCH_SIZE = 4;
const BUFFER = 2;
const BIT_MORE = 1;

export const Slider = ({
  children: slides,
  initialSlide = 0,
  onSliderUpdate,
  showImageCount = true,
}: IGallerySliderProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialSlide);
  const [loadedSlides, setLoadedSlides] = useState(
    slides.slice(
      0,
      initialSlide + 1 > BATCH_SIZE ? initialSlide + 1 : BATCH_SIZE,
    ),
  );
  const totalItems = slides.length;

  const carouselInnerRef = useRef<HTMLDivElement>(null);
  const prevButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);

  const shouldLoadMore = useCallback(
    (index: number) => {
      return (
        loadedSlides.length < slides.length &&
        index >= loadedSlides.length - BUFFER
      );
    },
    [loadedSlides.length, slides.length],
  );

  const loadMoreSlides = useCallback(
    (index: number) => {
      if (shouldLoadMore(index)) {
        const newItems = slides.slice(0, loadedSlides.length + BATCH_SIZE);
        setLoadedSlides([...newItems]);
      }
    },
    [loadedSlides.length, shouldLoadMore, slides],
  );

  const scrollToSlide = useCallback(
    (index: number, behavior: globalThis.ScrollBehavior = "smooth") => {
      const child = carouselInnerRef?.current?.children[index] as HTMLElement;
      if (child && carouselInnerRef?.current?.scrollTo) {
        const offsetLeft = child.offsetLeft;
        carouselInnerRef.current.scrollTo({
          left: offsetLeft,
          behavior: behavior,
        });
      }
    },
    [],
  );

  const previousSlide = useCallback(() => {
    if (currentIndex - 1 > -1) {
      scrollToSlide(currentIndex - 1);
    }
  }, [currentIndex, scrollToSlide]);

  const nextSlide = useCallback(() => {
    if (currentIndex + 1 < totalItems) {
      scrollToSlide(currentIndex + 1);
    }
  }, [currentIndex, scrollToSlide, totalItems]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        previousSlide();
        prevButtonRef.current?.focus();
      } else if (event.key === "ArrowRight") {
        nextSlide();
        nextButtonRef.current?.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [previousSlide, nextSlide]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    const handleScroll = () => {
      const topThreshold =
        window.innerWidth + window.innerWidth * 0.2 + BIT_MORE;
      if (carouselInnerRef.current) {
        const children = [...carouselInnerRef.current.children];
        const index = children.findIndex((child) => {
          const rect = child.getBoundingClientRect();
          return rect.left >= 0 && rect.right <= topThreshold;
        });

        clearTimeout(timeout);

        if (index !== -1) {
          setCurrentIndex(index);

          if (shouldLoadMore(index)) {
            loadMoreSlides(index);
          }

          timeout = setTimeout(() => {
            onSliderUpdate?.(index);
          }, 150);
        }
      }
    };

    const carousel = carouselInnerRef.current;
    carousel?.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      carousel?.removeEventListener("scroll", handleScroll);
    };
  }, [loadMoreSlides, onSliderUpdate, shouldLoadMore]);

  useEffect(() => {
    scrollToSlide(initialSlide, "instant" as ScrollBehavior);
  }, [initialSlide, scrollToSlide]);

  return (
    <Styled.GallerySliderWrapper>
      <Styled.GallerySliderInner ref={carouselInnerRef}>
        {loadedSlides.map((child, index) => (
          <Styled.GallerySliderItem key={index}>
            {child}
          </Styled.GallerySliderItem>
        ))}
      </Styled.GallerySliderInner>
      {currentIndex > 0 && (
        <Styled.PreviousButton
          ref={prevButtonRef}
          type="button"
          data-gui="carousel-previous-icon"
          aria-label="Previous image"
          onClick={previousSlide}
        >
          <IconChevronLeft title="" size={18} />
        </Styled.PreviousButton>
      )}
      {currentIndex < totalItems - 1 && (
        <Styled.NextButton
          ref={nextButtonRef}
          type="button"
          data-gui="carousel-next-icon"
          aria-label="Next image"
          onClick={nextSlide}
        >
          <IconChevronRight title="" size={18} />
        </Styled.NextButton>
      )}
      {showImageCount && (
        <Styled.GallerySliderCounter>{`${
          currentIndex + 1
        } / ${totalItems}`}</Styled.GallerySliderCounter>
      )}
    </Styled.GallerySliderWrapper>
  );
};
